import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { 
  Shield, FileText, CheckCircle, ArrowRight, Target, Activity, 
  Zap, ClipboardCheck, AlertTriangle, BarChart3, Settings, Layers,
  FileCheck, BookOpen, Cpu, Lock, Award, TrendingUp
} from 'lucide-react';

export const TenderSupport = () => {
  return (
    <Layout>
      <SeoHead
        title="Tender Support & Prequalification | EnergiPro Solutions"
        description="EnergiPro tender support: prequalification documentation, technical capability packages, compliance matrices, and vendor registration assistance for NOCs, IOCs, and EPCs."
        canonicalPath="/tender-support"
      />
      {/* ===== HERO ===== */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.1) 40px, rgba(255,255,255,0.1) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.1) 40px, rgba(255,255,255,0.1) 41px)' }}></div>
        <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-orange-600/5 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange-600/15 border border-orange-500/30 px-4 py-2 mb-6 rounded-sm">
              <Award size={14} className="text-orange-400" />
              <span className="text-orange-400 text-xs font-bold uppercase tracking-wider">Tender Support & Technical Capability</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Engineering-Driven Tender Response —{' '}
              <span className="text-orange-500">Structured, Validated, Defensible</span>
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 max-w-2xl leading-relaxed">
              We support complex oilfield tenders with structured RFQ processing, pre-execution validation, 
              risk-aware configuration, and standardized technical documentation — reducing evaluation risk 
              and building client confidence.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" href="/request-quote" className="!px-10">
                Request Technical Proposal
              </Button>
              <Button size="lg" variant="outline-light" href="/contact?demo=true">
                <Activity size={16} className="mr-2" />
                See Live Demo
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== SECTION 1: ENGINEERING APPROACH ===== */}
      <Section background="white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Settings size={14} /> Engineering Approach
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Structured Engineering Process for Every Tender</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Every tender response follows a disciplined engineering process — from initial scope capture 
              through validation to final execution planning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 */}
            <div className="relative bg-white border border-slate-200 p-6 rounded-sm hover:shadow-lg hover:border-orange-300 transition-all group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-t-sm"></div>
              <div className="w-10 h-10 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center font-bold text-sm mb-4">01</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Scope Capture & Classification</h3>
              <p className="text-slate-600 text-sm">
                Structured RFQ intake captures service type, operating conditions (pressure, flow, H₂S, sand), 
                location, timeline, and scope model — ensuring no ambiguity in requirements.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative bg-white border border-slate-200 p-6 rounded-sm hover:shadow-lg hover:border-orange-300 transition-all group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-t-sm"></div>
              <div className="w-10 h-10 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center font-bold text-sm mb-4">02</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Engineering Validation</h3>
              <p className="text-slate-600 text-sm">
                Rule-based engine validates equipment selection against stated conditions. Pressure ratings, 
                material compatibility (NACE MR0175), and capacity calculations verified automatically.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative bg-white border border-slate-200 p-6 rounded-sm hover:shadow-lg hover:border-orange-300 transition-all group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-t-sm"></div>
              <div className="w-10 h-10 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center font-bold text-sm mb-4">03</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Risk Profiling & Simulation</h3>
              <p className="text-slate-600 text-sm">
                Digital twin analysis generates operational confidence score, identifies risk factors 
                (pressure, H₂S, sand, capacity), and recommends mitigations before mobilization.
              </p>
            </div>

            {/* Step 4 */}
            <div className="relative bg-white border border-slate-200 p-6 rounded-sm hover:shadow-lg hover:border-orange-300 transition-all group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-600 to-orange-400 rounded-t-sm"></div>
              <div className="w-10 h-10 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center font-bold text-sm mb-4">04</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Execution Planning</h3>
              <p className="text-slate-600 text-sm">
                Mobilization timeline, crew composition, logistics requirements, and operational sequence 
                documented and validated — ready for client review and approval.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== SECTION 2: DIGITAL CAPABILITY ===== */}
      <Section background="light">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Cpu size={14} /> Digital Capability
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Integrated Digital Engineering Platform</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Purpose-built systems that reduce response time, eliminate configuration errors, 
              and demonstrate engineering rigor during tender evaluation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Smart RFQ System */}
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-xl transition-shadow">
              <div className="bg-slate-900 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                    <ClipboardCheck size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Smart RFQ System</h3>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>6-step structured intake — service, conditions, scope, location, timeline, notes</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>15 service categories covering full oilfield operations</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Condition-driven equipment selection (pressure, flow, H₂S, sand)</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Auto-generated technical proposals with engineering justification</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Validation Engine */}
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-xl transition-shadow">
              <div className="bg-slate-900 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                    <Shield size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Validation Engine</h3>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Rule-based pressure rating verification (API 6A compliance)</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Material compatibility checks for sour service (NACE MR0175)</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Capacity calculations — separator sizing, gas handling, flow rates</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Safety system validation — gas detection, emergency shutdown, flaring</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Scenario Analysis */}
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-xl transition-shadow">
              <div className="bg-slate-900 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                    <BarChart3 size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Scenario Analysis</h3>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Digital twin simulation — What-If analysis on configuration changes</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>4-axis confidence scoring (Pressure, Safety, Capacity, Environment)</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Risk profiling with triggered factors and mitigation recommendations</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                    <span>Equipment removal impact analysis — compliance warnings in real-time</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <Link
              to="/contact?demo=true"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-orange-600 text-white font-bold text-sm rounded-sm hover:bg-orange-700 transition-colors shadow-lg shadow-orange-600/20"
            >
              <Activity size={16} />
              Launch Live Demo — See Full Workflow
            </Link>
          </div>
        </div>
      </Section>

      {/* ===== SECTION 3: DELIVERABLES ===== */}
      <Section background="white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <FileText size={14} /> Technical Deliverables
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Standardized Outputs for Tender Submission</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              All documentation follows consistent structure, uses professional engineering language, 
              and is formatted for direct inclusion in tender packages.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Technical Response Document */}
            <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center shrink-0">
                  <FileCheck size={24} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Preliminary Technical Response</h3>
                  <p className="text-slate-600 text-sm mb-3">7-section structured document covering executive summary, project conditions, equipment configuration, engineering justification, execution model, engineering notes, and assumptions.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">PDF Export</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">7 Sections</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Standards-Referenced</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RFQ Draft */}
            <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center shrink-0">
                  <ClipboardCheck size={24} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Client RFQ Draft</h3>
                  <p className="text-slate-600 text-sm mb-3">Formal request document with service declaration, operational conditions table, scope definition, location logistics, required timeline, and special requirements — ready for client formatting.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Structured Format</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Tabular Data</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Client-Ready</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Engineering Validation Report */}
            <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center shrink-0">
                  <Shield size={24} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Engineering Validation Report</h3>
                  <p className="text-slate-600 text-sm mb-3">Automated validation output documenting pressure compliance, material suitability, capacity adequacy, safety system coverage, and operational confidence assessment.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Rule-Based</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Confidence Score</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Risk Factors</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Risk Profile & Mitigation Plan */}
            <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center shrink-0">
                  <AlertTriangle size={24} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Risk Profile & Mitigation Plan</h3>
                  <p className="text-slate-600 text-sm mb-3">Digital twin output identifying risk factors (pressure, H₂S, sand, environment), criticality levels, triggered conditions, and recommended mitigation strategies.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">5-Factor Analysis</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Criticality Rating</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Mitigations</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Equipment Configuration Sheet */}
            <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center shrink-0">
                  <Layers size={24} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Equipment Configuration Sheet</h3>
                  <p className="text-slate-600 text-sm mb-3">Grouped by functional system — pressure control, separation, safety, sand management, flow measurement, chemical treatment, and logistics — with specifications and standards.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">System-Grouped</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">API/ASME Refs</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Spec Sheets</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobilization & Execution Plan */}
            <div className="bg-white border border-slate-200 p-6 rounded-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center shrink-0">
                  <TrendingUp size={24} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Mobilization & Execution Plan</h3>
                  <p className="text-slate-600 text-sm mb-3">Operational sequence from acceptance through demobilization — staging, transport, rig-up, commissioning, operations, data delivery, demob — with timeline estimates.</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Timeline</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Crew Plan</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Logistics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== SECTION 4: COMPLIANCE ===== */}
      <Section background="dark">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-orange-600/20 text-orange-400 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Lock size={14} /> HSE & Standards Compliance
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Built-In Compliance & Standards Adherence</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Every output references applicable standards, maintains HSE alignment, 
              and supports client audit requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <Shield className="text-orange-500 mb-4" size={28} />
              <h4 className="text-white font-bold mb-2">API Standards Integration</h4>
              <p className="text-slate-400 text-sm">Equipment selected per API 6A (wellhead/tree), API RP 14E (piping), API 12J (separators). All selections traceable to applicable standard.</p>
            </div>

            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <Lock className="text-orange-500 mb-4" size={28} />
              <h4 className="text-white font-bold mb-2">NACE MR0175 Compliance</h4>
              <p className="text-slate-400 text-sm">Automatic sour service material checks triggered when H₂S is declared. Equipment compatibility verified against NACE requirements.</p>
            </div>

            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <FileText className="text-orange-500 mb-4" size={28} />
              <h4 className="text-white font-bold mb-2">ASME B31.3 Process Piping</h4>
              <p className="text-slate-400 text-sm">Process piping and connection specifications validated against ASME B31.3 requirements for pressure and temperature service.</p>
            </div>

            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <AlertTriangle className="text-orange-500 mb-4" size={28} />
              <h4 className="text-white font-bold mb-2">HSE Performance Data</h4>
              <p className="text-slate-400 text-sm">TRIR 0.00, LTIR 0.00, 185K+ manhours tracked. JSA compliance 100%. Near-miss reporting active. Data available for prequalification.</p>
            </div>

            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <BookOpen className="text-orange-500 mb-4" size={28} />
              <h4 className="text-white font-bold mb-2">IMS Framework</h4>
              <p className="text-slate-400 text-sm">Integrated Management System certified to ISO 9001:2015, ISO 14001:2015, and ISO 45001:2018. Full third-party certification achieved July 2026.</p>
            </div>

            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <Award className="text-orange-500 mb-4" size={28} />
              <h4 className="text-white font-bold mb-2">Audit-Ready Documentation</h4>
              <p className="text-slate-400 text-sm">All equipment maintains current pressure test certificates, third-party inspection records, and maintenance logs. Available upon request.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== WHY THIS MATTERS FOR TENDERS ===== */}
      <Section background="white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Why This Matters During Tender Evaluation</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Procurement teams evaluate capability, risk, and readiness. Our platform addresses all three.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-700 rounded-full flex items-center justify-center mx-auto mb-5">
                <Target size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Demonstrates Capability</h3>
              <p className="text-slate-600 text-sm">
                Structured technical responses show engineering depth — not just equipment lists. 
                Evaluators see validated configurations with justification for every selection.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mx-auto mb-5">
                <Shield size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Reduces Perceived Risk</h3>
              <p className="text-slate-600 text-sm">
                Pre-execution validation, risk profiling, and confidence scoring demonstrate that 
                operations are planned — not improvised. Standards referenced throughout.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center mx-auto mb-5">
                <TrendingUp size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Builds Client Confidence</h3>
              <p className="text-slate-600 text-sm">
                Digital twin analysis, What-If scenarios, and execution planning show a contractor 
                that thinks ahead — reducing evaluation committee uncertainty.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== FINAL CTA ===== */}
      <Section background="navy" className="border-t-2 border-orange-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Strengthen Your Next Tender Response?
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            Get a validated equipment configuration, engineering justification, and risk assessment — 
            structured for direct inclusion in your tender package.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Button size="lg" href="/request-quote">
              Request Technical Proposal
            </Button>
            <Button size="lg" variant="outline-light" href="/request-quote">
              Start Smart RFQ
            </Button>
            <Button size="lg" variant="outline-light" href="/request-quote">
              Book Engineering Discussion
            </Button>
          </div>
        </div>
      </Section>
    </Layout>
  );
};
